import {
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import MultiSelectDropdown from "../../../../components/multi-select-dropdown/multi-select-dropdown";

interface ExternalContact {
  fullName: string;
  email: string;
}

type Props = {
  formik: any;
  clientAllUsers: any;
};

const Contacts = (props: Props) => {
  const { formik, clientAllUsers } = props;
  const [showVisionUsers, setShowVisionUsers] = useState(
    formik.values.contacts.showVisionUsers
  );
  const [showExtraContacts, setShowExtraContacts] = useState(
    formik.values.contacts.showExtraContacts
  );

  useEffect(() => {
    setShowVisionUsers(formik.values.contacts.showVisionUsers);
  }, [formik.values.contacts.showVisionUsers]);

  useEffect(() => {
    setShowExtraContacts(formik.values.contacts.showExtraContacts);
  }, [formik.values.contacts.showExtraContacts]);

  const handleVisionUsersChange = (checked: boolean) => {
    setShowVisionUsers(checked);
    formik.setFieldValue("contacts.showVisionUsers", checked);
    if (!checked) {
      formik.setFieldValue("contacts.contactIds", []);
    }
  };

  const handleExtraContactsChange = (checked: boolean) => {
    setShowExtraContacts(checked);
    formik.setFieldValue("contacts.showExtraContacts", checked);
    if (!checked) {
      formik.setFieldValue("contacts.externalContacts", [
        { fullName: "", email: "" },
      ]);
    } else {
      // When enabling external contacts, trigger validation
      setTimeout(() => {
        formik.setFieldTouched("contacts.externalContacts", true);
        formik.validateField("contacts.externalContacts");
      }, 100);
    }
  };

  const addExternalContact = () => {
    const currentContacts = formik.values.contacts.externalContacts;
    if (currentContacts.length < 5) {
      const newContacts = [...currentContacts, { fullName: "", email: "" }];
      formik.setFieldValue("contacts.externalContacts", newContacts);
      formik.setFieldTouched("contacts.externalContacts", true);

      setTimeout(() => {
        formik.validateField("contacts.externalContacts");
      }, 0);
    }
  };

  const removeExternalContact = (index: number) => {
    const currentContacts = formik.values.contacts.externalContacts;
    const newContacts = currentContacts.filter(
      (_: any, i: number) => i !== index
    );
    formik.setFieldValue("contacts.externalContacts", newContacts);
    formik.setFieldTouched("contacts.externalContacts", true);
  };

  const updateExternalContact = (
    index: number,
    field: keyof ExternalContact,
    value: string
  ) => {
    const currentContacts = [...formik.values.contacts.externalContacts];
    currentContacts[index][field] = value;
    formik.setFieldValue("contacts.externalContacts", currentContacts);
    formik.setFieldTouched("contacts.externalContacts", true);
    formik.setFieldTouched(`contacts.externalContacts.${index}.${field}`, true);

    setTimeout(() => {
      formik.validateField("contacts.externalContacts");
    }, 0);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={formik.values.contacts.includeRequester}
            onChange={(e) => {
              formik.setFieldValue(
                "contacts.includeRequester",
                e.target.checked
              );
              formik.setFieldTouched("contacts.includeRequester", true);
            }}
            name="includeRequester"
            disabled={!formik.values.clientId}
          />
        }
        label="Requestor"
      />
      {formik.values.contacts.includeRequester && formik.values.requesterId && (
        <Typography
          sx={{
            padding: "3px 14px",
            border: "1px solid",
            borderColor: "primary.main",
            borderRadius: "100px",
            color: "primary.main",
            display: "inline-block",
            width: "fit-content",
            margin: 1,
            ml: 4,
          }}
        >
          {
            clientAllUsers?.filter(
              (client: any) => client.id === formik.values.requesterId
            )?.[0]?.fullName
          }
        </Typography>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={showVisionUsers}
            onChange={(e) => handleVisionUsersChange(e.target.checked)}
            name="visionUsers"
            disabled={!formik.values.clientId}
          />
        }
        label="Vision User(s)"
      />
      {showVisionUsers && (
        <Box sx={{ ml: 4, mt: 1 }}>
          <MultiSelectDropdown
            label=""
            options={
              clientAllUsers?.map((client: any) => ({
                id: client.id,
                label: client.fullName,
              })) || []
            }
            selectedValues={formik.values.contacts.contactIds}
            onChange={(selected) => {
              formik.setFieldValue("contacts.contactIds", selected);
              formik.setFieldTouched("contacts.contactIds", true);
            }}
            placeholder="Search and select vision users..."
            width="100%"
            maxHeight={200}
          />
          {formik.touched.contacts?.contactIds &&
            formik.errors.contacts?.contactIds && (
              <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                {String(formik.errors.contacts.contactIds) ||
                  "This field can't be empty"}
              </Typography>
            )}
        </Box>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={showExtraContacts}
            onChange={(e) => handleExtraContactsChange(e.target.checked)}
            name="externalContacts"
            disabled={!formik.values.clientId}
          />
        }
        label="External Contact(s)"
      />
      {showExtraContacts && (
        <Box sx={{ ml: 4, mt: 1 }}>
          {formik.values.contacts.externalContacts.map(
            (contact: ExternalContact, index: number) => (
              <Box
                key={index}
                sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}
              >
                <TextField
                  label="Name"
                  value={contact.fullName}
                  onChange={(e) =>
                    updateExternalContact(index, "fullName", e.target.value)
                  }
                  onBlur={() =>
                    formik.setFieldTouched(
                      `contacts.externalContacts.${index}.fullName`,
                      true
                    )
                  }
                  size="small"
                  sx={{ minWidth: "348px", width: "500px" }}
                  error={
                    !!(formik.errors.contacts?.externalContacts?.[index] as any)
                      ?.fullName &&
                    !!(
                      formik.touched.contacts?.externalContacts?.[index] as any
                    )?.fullName
                  }
                  helperText={
                    (formik.touched.contacts?.externalContacts?.[index] as any)
                      ?.fullName &&
                    (formik.errors.contacts?.externalContacts?.[index] as any)
                      ?.fullName
                      ? String(
                          (
                            formik.errors.contacts?.externalContacts?.[
                              index
                            ] as any
                          )?.fullName || ""
                        )
                      : ""
                  }
                />
                <TextField
                  label="Email"
                  value={contact.email}
                  onChange={(e) =>
                    updateExternalContact(index, "email", e.target.value)
                  }
                  onBlur={() =>
                    formik.setFieldTouched(
                      `contacts.externalContacts.${index}.email`,
                      true
                    )
                  }
                  size="small"
                  sx={{ minWidth: "348px", width: "500px" }}
                  error={
                    !!(formik.errors.contacts?.externalContacts?.[index] as any)
                      ?.email &&
                    !!(
                      formik.touched.contacts?.externalContacts?.[index] as any
                    )?.email
                  }
                  helperText={
                    (formik.touched.contacts?.externalContacts?.[index] as any)
                      ?.email &&
                    (formik.errors.contacts?.externalContacts?.[index] as any)
                      ?.email
                      ? String(
                          (
                            formik.errors.contacts?.externalContacts?.[
                              index
                            ] as any
                          )?.email || ""
                        )
                      : ""
                  }
                />
                <IconButton
                  onClick={() => removeExternalContact(index)}
                  size="small"
                  sx={{ color: "error.main" }}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Box>
            )
          )}
          {formik.values.contacts.externalContacts.length < 5 && (
            <Button
              startIcon={<AddIcon />}
              onClick={addExternalContact}
              sx={{
                color: "primary.main",
                textTransform: "none",
                fontSize: "14px",
                p: 0,
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "transparent",
                  textDecoration: "none",
                },
              }}
            >
              ADD ADDITIONAL CONTACT
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Contacts;
